import type { UseFormReturn } from 'react-hook-form'
import { Tag, Gift, Truck, Mail, Star } from 'lucide-react'
import { CardSelector } from './CardSelector'
import type { CardOption } from './CardSelector'
import {
  ACTION_TYPE_LABELS,
  ACTION_TYPE_DESCRIPTIONS,
  type StepActionData,
  type ActionType,
} from '../types'

const ACTION_CARDS: CardOption<ActionType>[] = [
  { value: 'coupon', label: ACTION_TYPE_LABELS.coupon, description: ACTION_TYPE_DESCRIPTIONS.coupon, icon: <Tag size={24} /> },
  { value: 'physical_gift_voucher', label: ACTION_TYPE_LABELS.physical_gift_voucher, description: ACTION_TYPE_DESCRIPTIONS.physical_gift_voucher, icon: <Gift size={24} /> },
  { value: 'free_shipping_flag', label: ACTION_TYPE_LABELS.free_shipping_flag, description: ACTION_TYPE_DESCRIPTIONS.free_shipping_flag, icon: <Truck size={24} /> },
  { value: 'email_notification', label: ACTION_TYPE_LABELS.email_notification, description: ACTION_TYPE_DESCRIPTIONS.email_notification, icon: <Mail size={24} /> },
  { value: 'points_bonus', label: ACTION_TYPE_LABELS.points_bonus, description: ACTION_TYPE_DESCRIPTIONS.points_bonus, icon: <Star size={24} /> },
]

interface StepActionFormProps {
  form: UseFormReturn<StepActionData>
  onSubmit: () => void
  onBack: () => void
}

export function StepActionForm({ form, onSubmit, onBack }: StepActionFormProps) {
  const watchedActionType = form.watch('action_type')
  const watchedShippingCondition = form.watch('shipping_condition')

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Action type card selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Tipo de beneficio *</label>
        <CardSelector<ActionType>
          options={ACTION_CARDS}
          value={watchedActionType}
          onChange={(val) => form.setValue('action_type', val, { shouldValidate: true })}
          error={form.formState.errors.action_type?.message}
        />
      </div>

      {/* Coupon config */}
      {watchedActionType === 'coupon' && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-medium text-gray-700">Configuración del cupón</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de descuento</label>
              <select {...form.register('coupon_discount_type')} className="input">
                <option value="percent">Porcentaje</option>
                <option value="fixed_cart">Monto fijo</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
              <input type="number" {...form.register('coupon_amount')} className="input" min={0} />
            </div>
            {form.watch('coupon_discount_type') === 'percent' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descuento máximo ($)</label>
                <input type="number" {...form.register('coupon_max_discount')} className="input" min={0} />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prefijo de código</label>
              <input {...form.register('coupon_code_prefix')} className="input" placeholder="CLUB" />
            </div>
          </div>
        </div>
      )}

      {/* Physical gift voucher config */}
      {watchedActionType === 'physical_gift_voucher' && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-medium text-gray-700">Configuración del vale físico</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción del regalo</label>
            <input {...form.register('gift_description')} className="input" placeholder="Ej: Torta de chocolate mini" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Días de validez</label>
            <input type="number" {...form.register('gift_valid_days')} className="input w-32" min={1} />
          </div>
        </div>
      )}

      {/* Free shipping config */}
      {watchedActionType === 'free_shipping_flag' && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-medium text-gray-700">Configuración de despacho gratis</h3>
          <div className="space-y-2">
            {(['first_purchase', 'min_amount', 'permanent'] as const).map((cond) => (
              <label key={cond} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value={cond}
                  checked={watchedShippingCondition === cond}
                  onChange={() => form.setValue('shipping_condition', cond)}
                  className="border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  {cond === 'first_purchase' && 'Primera compra'}
                  {cond === 'min_amount' && 'Monto mínimo'}
                  {cond === 'permanent' && 'Permanente'}
                </span>
              </label>
            ))}
          </div>
          {watchedShippingCondition === 'min_amount' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monto mínimo ($)</label>
              <input type="number" {...form.register('shipping_min_amount')} className="input w-40" min={0} />
            </div>
          )}
        </div>
      )}

      {/* Points bonus config */}
      {watchedActionType === 'points_bonus' && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-medium text-gray-700">Configuración de bonus</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Puntos a otorgar</label>
            <input type="number" {...form.register('bonus_points')} className="input w-40" min={1} />
          </div>
        </div>
      )}

      {/* Email notification config */}
      {watchedActionType === 'email_notification' && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-medium text-gray-700">Configuración del email</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
            <select {...form.register('email_template')} className="input">
              <option value="">Seleccionar...</option>
              <option value="birthday">Cumpleaños</option>
              <option value="welcome">Bienvenida</option>
              <option value="promotion">Promoción</option>
              <option value="custom">Personalizado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Asunto</label>
            <input {...form.register('email_subject')} className="input" placeholder="Ej: ¡Feliz cumpleaños!" />
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button type="button" onClick={onBack} className="btn btn-secondary">
          Anterior
        </button>
        <button type="submit" className="btn btn-primary">
          Siguiente
        </button>
      </div>
    </form>
  )
}
